import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { api, type Client, type SuspiciousDataClient } from '../../lib/api';
import { unwrapApi } from '../../lib/unwrapApi';
import { DismissAlertModal } from './DismissAlertModal';

interface DuplicateReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: SuspiciousDataClient;
  onClientClick?: (clientId: string) => void;
}

export function DuplicateReviewModal({
  isOpen,
  onClose,
  client,
  onClientClick,
}: DuplicateReviewModalProps) {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showDismissModal, setShowDismissModal] = useState(false);

  // Fetch all clients with the same name
  const { data: duplicates, isLoading } = useQuery({
    queryKey: ['clients', 'duplicates', client.clientName],
    queryFn: async () => {
      // Fetch all active clients and filter for exact name match
      const result = await api.clients.list({ activeOnly: true, limit: 10000 });
      // Filter to only exact matches (case-insensitive)
      return result.data?.filter(
        (c: Client) => c.clientName.toLowerCase().trim() === client.clientName.toLowerCase().trim()
      ) || [];
    },
    enabled: isOpen,
  });

  // Mutation to rename a client
  const renameMutation = useMutation({
    mutationFn: async ({ id, newName }: { id: string; newName: string }) => {
      return api.clients.update(id, { clientName: newName }).then(unwrapApi);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setEditingId(null);
      setEditValue('');
    },
  });

  const handleStartEdit = (c: Client) => {
    setEditingId(c.id);
    setEditValue(c.clientName);
  };

  const handleSaveEdit = () => {
    if (editingId && editValue.trim()) {
      renameMutation.mutate({ id: editingId, newName: editValue.trim() });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleViewClient = (clientId: string) => {
    onClientClick?.(clientId);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
        <div
          className="bg-white w-full sm:w-[560px] sm:rounded-2xl sm:max-h-[90vh] max-h-[95vh] overflow-hidden flex flex-col animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-forest-800 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xl">👥</span>
                <h2 className="font-display text-lg font-semibold">Duplicate Client Names</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Description */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex gap-3">
                <span className="text-amber-500 text-lg">⚠️</span>
                <div>
                  <p className="text-sm text-amber-800 font-medium">
                    These clients have the same name
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Review the duplicates below and either rename them to be unique, or dismiss this alert if they are intentionally named the same.
                  </p>
                </div>
              </div>
            </div>

            {/* Duplicate List */}
            <div>
              <h3 className="text-sm font-medium text-forest-700 mb-3">
                Clients named "{client.clientName}"
              </h3>

              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-20 bg-sage-100 rounded-xl animate-pulse" />
                  ))}
                </div>
              ) : duplicates && duplicates.length > 0 ? (
                <div className="space-y-3">
                  {duplicates.map((dup: Client) => (
                    <div
                      key={dup.id}
                      className="border border-sage-200 rounded-xl p-4 hover:border-sage-300 transition-colors"
                    >
                      {editingId === dup.id ? (
                        /* Edit mode */
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-forest-700 mb-1">
                              Client Name
                            </label>
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={handleKeyDown}
                              autoFocus
                              className="w-full px-3 py-2 rounded-xl border-2 border-primary-500 focus:outline-none"
                              placeholder="Enter new name..."
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={handleSaveEdit}
                              disabled={!editValue.trim() || editValue === dup.clientName || renameMutation.isPending}
                            >
                              {renameMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View mode */
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-forest-900">{dup.clientName}</p>
                              {dup.id === client.id && (
                                <Badge variant="info" size="sm">Selected</Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-sm text-sage-600">
                              <span className="font-mono text-xs">{dup.id}</span>
                              {dup.routeId && (
                                <>
                                  <span>•</span>
                                  <span>Route: {dup.routeId}</span>
                                </>
                              )}
                              {dup.techId && (
                                <>
                                  <span>•</span>
                                  <span>Tech: {dup.techId}</span>
                                </>
                              )}
                            </div>
                            {dup.address && (
                              <p className="text-sm text-sage-500 mt-1 truncate">
                                {[dup.address.city, dup.address.state].filter(Boolean).join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewClient(dup.id)}
                              title="View client details"
                            >
                              View
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleStartEdit(dup)}
                              title="Rename client"
                            >
                              Rename
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sage-500">No duplicate clients found.</p>
                </div>
              )}
            </div>

            {/* Error Display */}
            {renameMutation.isError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-sm text-red-700">
                  Failed to rename client. Please try again.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-sage-200 px-6 py-4 flex gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowDismissModal(true)}
              className="flex-1"
            >
              Dismiss Alert
            </Button>
            <Button variant="primary" onClick={onClose} className="flex-1">
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Nested Dismiss Modal */}
      <DismissAlertModal
        isOpen={showDismissModal}
        onClose={() => setShowDismissModal(false)}
        alertType="duplicate_name"
        entityId={client.id}
        clientName={client.clientName}
        issueDescription={`This client has the same name as ${(duplicates?.length || 1) - 1} other client(s)`}
        onDismissed={() => {
          setShowDismissModal(false);
          onClose();
        }}
      />
    </>
  );
}
